/**
 * PaymentCheckoutPage.jsx - Checkout page for payment links
 *
 * Uses CheckoutProvider for ui_mode: 'custom' with line items on receipts
 */
import { useState, useEffect } from 'react';
import { CheckoutProvider } from '@stripe/react-stripe-js/checkout';
import { Store } from 'lucide-react';
import PaymentOrderSummary from './PaymentOrderSummary';
import PaymentTipSelector from './PaymentTipSelector';
import PaymentCheckoutForm from './PaymentCheckoutForm';
import { usePaymentData } from './PaymentApp';

const formatAmount = (amountCents, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
};

// Stripe logo for footer
const StripeLogo = () => (
  <svg width="49" height="20" viewBox="0 0 49 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M48.4718 10.3338C48.4718 6.91463 46.8434 4.23438 43.7634 4.23438C40.6701 4.23438 38.7617 6.91463 38.7617 10.3071C38.7617 14.3068 41.0051 16.3332 44.2184 16.3332C45.7801 16.3332 46.9551 15.9598 47.8501 15.4264V12.6661C46.9551 13.1461 45.9267 13.4395 44.6184 13.4395C43.3367 13.4395 42.2017 12.9862 42.0551 11.4112H48.4451C48.4451 11.2378 48.4718 10.6245 48.4718 10.3338ZM42.0151 8.89447C42.0151 7.38613 42.9234 6.8128 43.7501 6.8128C44.5501 6.8128 45.4051 7.38613 45.4051 8.89447H42.0151Z"
      fill="#6772E5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M33.5234 4.23438C32.2284 4.23438 31.3867 4.84771 30.9201 5.26104L30.7467 4.48771H27.9067V19.9999L31.1467 19.3066L31.1601 15.4531C31.6401 15.8131 32.3467 16.3332 33.5101 16.3332C35.8867 16.3332 38.0501 14.4398 38.0501 10.1871C38.0367 6.28796 35.8467 4.23438 33.5234 4.23438ZM32.7234 13.3328C31.9234 13.3328 31.4434 13.0261 31.1601 12.6661L31.1467 7.97946C31.4567 7.57946 31.9501 7.28613 32.7234 7.28613C33.9517 7.28613 34.8067 8.62113 34.8067 10.2938C34.8067 12.0065 33.9651 13.3328 32.7234 13.3328Z"
      fill="#6772E5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.6133 3.22656L25.8666 2.52656V-0.000976562L22.6133 0.685691V3.22656Z"
      fill="#6772E5"
    />
    <path d="M25.8666 4.48828H22.6133V16.0749H25.8666V4.48828Z" fill="#6772E5" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.0333 5.38802L18.8333 4.48802H16.0333V16.0747H19.2733V8.16135C20.02 7.18802 21.2733 7.36135 21.66 7.49468V4.48802C21.26 4.34135 19.78 4.06135 19.0333 5.38802Z"
      fill="#6772E5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.5267 1.54688L9.36667 2.22688L9.35333 12.8002C9.35333 14.8535 10.8867 16.3469 12.94 16.3469C14.0733 16.3469 14.9 16.1469 15.3533 15.9069V13.0935C14.9133 13.2669 12.5133 13.9602 12.5133 11.9069V7.42021H15.3533V4.48688H12.5133L12.5267 1.54688Z"
      fill="#6772E5"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.30667 7.97946C3.30667 7.47946 3.72 7.28613 4.4 7.28613C5.38667 7.28613 6.62667 7.57946 7.61333 8.10613V5.05279C6.53333 4.61946 5.46667 4.43946 4.4 4.43946C1.76 4.43946 0 5.86613 0 8.13279C0 11.6728 4.84 11.1128 4.84 12.6261C4.84 13.2128 4.32 13.4061 3.6 13.4061C2.52 13.4061 1.14667 12.9795 0.0533333 12.3928V15.4861C1.26667 16.0061 2.49333 16.2261 3.6 16.2261C6.30667 16.2261 8.17333 14.8395 8.17333 12.5461C8.16 8.71279 3.30667 9.39279 3.30667 7.97946Z"
      fill="#6772E5"
    />
  </svg>
);

const PaymentCheckoutPage = ({ stripePromise, clientSecret, sessionKey }) => {
  const {
    cachedLineItems,
    cachedSubtotalCents,
    cachedConvenienceFeeCents,
    cachedBusinessName,
    cachedCurrency,
    selectedTipCents,
    handleTipChange,
    formattedTotal,
    formattedSubtotal,
    isUpdatingSession,
  } = usePaymentData();

  const [isMobile, setIsMobile] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const handleChange = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    setIsSummaryOpen(!isMobile);
  }, [isMobile]);

  // TEST: Uncomment to test ErrorBoundary (must be after all hooks)
  // throw new Error('Testing ErrorBoundary');

  const businessName = cachedBusinessName || 'Payment';

  const displayLineItems = cachedLineItems.map((item) => ({
    ...item,
    unitAmountDisplay: formatAmount(item.unitAmountCents, cachedCurrency),
    lineTotalDisplay: formatAmount(item.lineTotalCents, cachedCurrency),
  }));

  return (
    <div className="app-container">
      <div className="checkout-content">
        {/* Mobile: Sticky Header */}
        {isMobile && (
          <header className="app-header">
            <div className="header-content">
              <div className="business-info">
                <div className="business-icon">
                  <Store size={24} strokeWidth={2} />
                </div>
                <span className="business-name">{businessName}</span>
              </div>
              <button
                type="button"
                className="header-details-btn"
                onClick={() => setIsSummaryOpen((prev) => !prev)}
                aria-expanded={isSummaryOpen}
              >
                Details
              </button>
            </div>
          </header>
        )}

        {/* Mobile: Product Summary */}
        {isMobile && (
          <div className="app-overview">
            <div className="product-summary">
              <h2 className="product-summary-name">Subtotal</h2>
              <p className="product-summary-total">{formattedSubtotal || '--'}</p>
              <button
                type="button"
                className="view-details-btn"
                onClick={() => setIsSummaryOpen((prev) => !prev)}
                aria-expanded={isSummaryOpen}
              >
                View details
              </button>
            </div>

            {isSummaryOpen && (
              <div className="summary-overlay" role="dialog" aria-modal="true">
                <div className="summary-overlay-backdrop" onClick={() => setIsSummaryOpen(false)} />
                <div className="summary-overlay-panel">
                  <div className="summary-overlay-header">
                    <div className="business-info">
                      <div className="business-icon">
                        <Store size={24} strokeWidth={2} />
                      </div>
                      <span className="business-name">{businessName}</span>
                    </div>
                    <button
                      type="button"
                      className="summary-close"
                      onClick={() => setIsSummaryOpen(false)}
                      aria-label="Close"
                    >
                      Close
                    </button>
                  </div>
                  <ul className="order-details-items">
                    {displayLineItems.map((item) => (
                      <li key={item.id} className="order-details-item">
                        <div className="line-item-content">
                          <p className="line-item-name">{item.name}</p>
                          <p className="line-item-price">{item.lineTotalDisplay}</p>
                        </div>
                        <div className="line-item-meta">
                          <span className="line-item-quantity">Qty {item.quantity}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="summary-total-row">
                    <span>Subtotal</span>
                    <span className="summary-total-amount">{formattedSubtotal || '--'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mobile: Tip Selector */}
        {isMobile && (
          <div className="mobile-tip-container">
            <PaymentTipSelector
              subtotalCents={cachedSubtotalCents}
              currentTipCents={selectedTipCents}
              currency={cachedCurrency}
              onTipChange={handleTipChange}
              isUpdating={isUpdatingSession}
            />
          </div>
        )}

        {/* Desktop: Order Summary */}
        {!isMobile && (
          <PaymentOrderSummary
            lineItems={displayLineItems}
            formattedTotal={formattedTotal}
            subtotalCents={cachedSubtotalCents}
            convenienceFeeCents={cachedConvenienceFeeCents}
            currentTipCents={selectedTipCents}
            currency={cachedCurrency}
            onTipChange={handleTipChange}
            isTipUpdating={isUpdatingSession}
            businessName={businessName}
          />
        )}

        {/* Checkout Form using CheckoutProvider */}
        <div className="app-payment">
          <CheckoutProvider key={sessionKey} stripe={stripePromise} options={{ clientSecret }}>
            <PaymentCheckoutForm />
          </CheckoutProvider>

          {/* Powered by Stripe Footer */}
          <footer className="stripe-footer">
            <div className="stripe-footer-powered">
              <span>Powered by</span>
              <StripeLogo />
            </div>
            <div className="stripe-footer-links">
              <a href="https://stripe.com/legal/consumer" target="_blank" rel="noopener noreferrer">
                Terms
              </a>
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckoutPage;
